import { useState } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'Do you really buy rental property with tenants in place?',
      answer: 'Yes, absolutely. We specialize in purchasing tenant occupied real estate. Your tenants can stay during and after the sale. We honor existing lease agreements and work with tenants to ensure a smooth transition. This is one of our core specialties.'
    },
    {
      question: 'How quickly can you close on a tenant occupied property?',
      answer: 'We typically close in 7-14 days from offer acceptance. However, we can be flexible based on your needs. If you need to close faster, we can often accommodate that. If you need more time, that works too. The timeline is up to you.'
    },
    {
      question: 'What if I have problem tenants or non-paying occupants?',
      answer: 'We buy properties with any tenant situation—including difficult tenants, non-paying occupants, or lease violations. We have the experience and resources to handle challenging situations after closing. You walk away from the problem, and we take it from there.'
    },
    {
      question: 'Do I need to make repairs before selling?',
      answer: 'No repairs required. We buy rental property in any condition—even if it needs significant work or has deferred maintenance. You don\'t need to fix anything or make any improvements. We make our offer based on current condition.'
    },
    {
      question: 'How do you determine your offer price?',
      answer: 'We consider multiple factors: property condition, location, rental income, tenant quality, lease terms, market comparables, and needed repairs. We make competitive cash offers that reflect fair market value for tenant occupied properties. Our offers account for the convenience of a fast, as-is sale.'
    },
    {
      question: 'What happens to the security deposits?',
      answer: 'Security deposits are properly transferred to us as the new owner, and this transfer is documented by the title company. Texas law has strict requirements for security deposit handling, and we ensure full compliance. Your tenants\' deposits are protected throughout the process.'
    },
    {
      question: 'Do I have to pay real estate commissions or fees?',
      answer: 'No commissions, no fees, no closing costs. The cash offer we make is what you receive. We cover all closing costs, title company fees, and other transaction expenses. There are no hidden fees or surprise deductions.'
    },
    {
      question: 'What types of rental properties do you buy?',
      answer: 'We buy all types: single-family rentals, duplexes, triplexes, fourplexes, small apartment buildings, large complexes, mixed-use properties, and townhouses. From one unit to 50+ units, we have the resources and experience to handle any size property.'
    },
    {
      question: 'Why do you use a title company for every transaction?',
      answer: 'Title companies provide essential protection for everyone involved. They conduct title searches, provide title insurance, handle escrow, prepare legal documents, manage lease transfers, and ensure all legal requirements are met. We insist on title company closings to protect you, your tenants, and us.'
    },
    {
      question: 'Can I sell multiple rental properties at once?',
      answer: 'Yes, we frequently purchase entire portfolios. If you own multiple rental properties, we can make one offer for all of them and close in a single transaction. This saves you time and simplifies the process significantly.'
    },
    {
      question: 'What if my property has complex lease situations?',
      answer: 'We handle all types of lease complexity: multiple units with different terms, month-to-month tenants, Section 8 vouchers, commercial and residential mix, varying expiration dates, and more. Our team reviews every lease and structures offers that work for your specific situation.'
    },
    {
      question: 'Do you buy properties with Section 8 tenants?',
      answer: 'Yes, we buy properties with Section 8 tenants. We understand the Housing Choice Voucher Program and work with local housing authorities to ensure proper procedures. Section 8 properties can be excellent investments, and we welcome them.'
    },
    {
      question: 'What if my tenants don\'t want to cooperate with the sale?',
      answer: 'We minimize tenant disruption. Typically, we only need one inspection, and we work around tenant schedules respectfully. Your tenants don\'t need to cooperate with showings or keep the property in showing condition. We make the process as easy as possible for everyone.'
    },
    {
      question: 'Can I keep collecting rent until closing?',
      answer: 'Yes, you continue collecting rent right up until closing day. You don\'t lose rental income during the sale process. This is a major advantage compared to traditional sales that often require vacant properties.'
    },
    {
      question: 'What if my property is underwater or has liens?',
      answer: 'We can often work with properties that have liens, judgments, or are underwater on the mortgage. The title company will identify all encumbrances during the title search, and we can discuss solutions. Every situation is different, so call us to discuss your specific circumstances.'
    },
    {
      question: 'How do I know you\'re legitimate and can actually close?',
      answer: 'We\'ve purchased over 200 rental properties totaling more than $120 million in transactions. We can provide references from past sellers, proof of funds, and our track record speaks for itself. Every transaction goes through a reputable title company, which provides additional verification and protection.'
    },
    {
      question: 'What areas of El Paso do you buy in?',
      answer: 'We buy tenant occupied properties throughout El Paso and surrounding areas—East, West, Central, North, and South El Paso. We also purchase properties in nearby communities. Location is not a barrier; we\'re interested in all areas.'
    },
    {
      question: 'Do I need to notify my tenants before contacting you?',
      answer: 'No, you don\'t need to notify tenants before contacting us. We can discuss your situation confidentially. Once we move forward with an offer, we\'ll help you determine the appropriate time and method for tenant notification in compliance with Texas law.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Get answers to common questions about selling tenant occupied property to T-Rex Real Estate.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between cursor-pointer"
              >
                <h3 className="text-lg font-bold text-gray-900 pr-8">{faq.question}</h3>
                <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-teal-600 text-2xl flex-shrink-0`}></i>
              </button>
              {openIndex === index && (
                <div className="px-8 pb-6">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Still Have Questions?</h3>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            We're happy to answer any questions about selling your tenant occupied property. Call us today for a free, no-obligation consultation.
          </p>
          <a 
            href="tel:+1-915-206-5814"
            className="inline-flex items-center bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill mr-2"></i>
            Call (915) 206-5814 Now
          </a>
        </div>
      </div>
    </section>
  );
}